import {useEffect} from "react";
import {useFormContext} from "react-hook-form";
import {Space} from "antd";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {ADD_ONS_UNAVAILABLE_UNDER_PWYW} from "../pricing/add-on-gate";
import {ProductVariantsFormFieldsProps, ProductVariantsFormValues} from "./types";
import {ProductVariantsToggleFormFields} from "./variant-toggle";
import {
  ProductVariantsListFormFields,
  ProductVariantsListFormFieldsProps,
} from "./variant-list";
import {OfferingFormAccordionItem} from "../../accordion/accordion-item";
import {ProductVariantsFormGroups} from "../../accordion/data";
import {ProductPricingFormFieldsType} from "../pricing/types";

export function ProductVariantFormFields({
  isVariantError,
  productCurrencies,
  productSelectedPriceType,
  hydratedRef,
}: ProductVariantsFormFieldsProps) {
  return (
    <Space direction="vertical" size={16} style={{display: "flex"}}>
      {ProductVariantsFormGroups.map((fg, index) => {
        if (fg.title === "Add-ons") {
          return (
            <OfferingFormAccordionItem
              key={fg.title}
              index={index}
              title={fg.title}
              isError={isVariantError}
            >
              <ProductVariantFormWrapper
                productCurrencies={productCurrencies}
                productSelectedPriceType={productSelectedPriceType}
                hydratedRef={hydratedRef}
              />
            </OfferingFormAccordionItem>
          );
        } else {
          return null;
        }
      })}
    </Space>
  );
}

type ProductVariantFormWrapperProps = {
  productCurrencies: ProductPricingFormFieldsType["currencies"];
  productSelectedPriceType: ProductVariantsFormFieldsProps["productSelectedPriceType"];
  hydratedRef?: ProductVariantsFormFieldsProps["hydratedRef"];
};

export function ProductVariantFormWrapper({
  productCurrencies,
  productSelectedPriceType,
  hydratedRef,
}: ProductVariantFormWrapperProps) {
  const {watch, setValue} = useFormContext<ProductVariantsFormValues>();

  const showVariants = watch("showVariants");
  const isPayWhatYouWant = IS_PWYW_ENABLED && productSelectedPriceType === "pwyw";

  // The Add-on list is only ever reached under Free or Single Fee, so it keeps the
  // two-value price type it was written for rather than being widened to a third case.
  // Widening it would be the quiet kind of wrong: the currency enforcer branches on
  // `paid` vs not, so a `pwyw` value would silently take the free path and clear an
  // Add-on's currencies.
  const addOnPriceType: ProductVariantsListFormFieldsProps["productSelectedPriceType"] =
    productSelectedPriceType === "pwyw" ? "free" : productSelectedPriceType;

  // The Pricing hierarchy rule: a customer naming their own price has no total for an
  // Add-on to be added to. The switch is forced off and locked rather than the section
  // being hidden, so a coach returning to this step sees why instead of a gap.
  useEffect(() => {
    if (isPayWhatYouWant && showVariants) {
      setValue("showVariants", false);
    }
  }, [isPayWhatYouWant, showVariants, setValue]);

  return (
    <Space size={16} direction="vertical" style={{display: "flex"}}>
      <ProductVariantsToggleFormFields
        unavailableReason={isPayWhatYouWant ? ADD_ONS_UNAVAILABLE_UNDER_PWYW : null}
      />
      {showVariants && !isPayWhatYouWant && (
        <ProductVariantsListFormFields
          productCurrencies={productCurrencies}
          productSelectedPriceType={addOnPriceType}
          hydratedRef={hydratedRef}
        />
      )}
    </Space>
  );
}
